<cs-header>
  <cs-header-title>{{ LL.editNutritionPlan.title() }}</cs-header-title>
</cs-header>
<cs-content>
  <cs-item-list>
    <!-- Warning alert -->
    <cs-info-card type="warning" [message]="LL.editNutritionPlan.description()" [dismissible]="false"></cs-info-card>

    <!-- Top controls row: Reset + Auto-adjust (fixed mode only) -->
    <div class="top-controls-row">
      <cs-button tertiary small [label]="LL.editNutritionPlan.resetRecommended()" icon="undo"
        (click)="handleReset()"></cs-button>
      @if (!isFlexible) {
      <div class="auto-adjust-row">
        <span class="auto-adjust-label">{{ LL.editNutritionPlan.autoAdjustLabel() }}</span>
        <cs-tooltip [text]="LL.editNutritionPlan.autoAdjustTooltip()"></cs-tooltip>
        <!-- [ariaLabel] names the switch: the tooltip beside it is a control of its own, so
             there is no row text the toggle can take its name from -->
        <cs-toggle [ariaLabel]="LL.editNutritionPlan.autoAdjustLabel()" [(checked)]="autoAdjust"></cs-toggle>
      </div>
      }
    </div>

    <!-- Calories section -->
    <div class="calories-section">
      <p class="section-title">
        {{ LL.editNutritionPlan.caloriesLabel() }}
        <help-icon name="info" helpId="@help:references.calorie-targets"></help-icon>
      </p>
      <cs-form-field>
        <cs-input-measure class="fixed-input" [(canonValue)]="dailyIntake" type="nutrient_kcal" [precisionOverride]="1"
          (canonValueChange)="onCaloriesChange()" input-sequence></cs-input-measure>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedIntake() }) }}</cs-note>
      </cs-form-field>
    </div>

    <!-- Divider -->
    <cs-item-divider></cs-item-divider>

    <!-- Macros section -->
    <div class="macros-section">
      <div class="macros-header">
        <p class="section-title">
          {{ LL.editNutritionPlan.macronutrients() }}
          <help-icon name="info" helpId="@help:references.protein-targets"></help-icon>
        </p>
      </div>

      <!-- Protein -->
      <cs-form-field [label]="LL.editNutritionPlan.proteinLabel()">
        <cs-input-measure class="fixed-input" [(canonValue)]="protein" type="nutrient_g" [precisionOverride]="1"
          (canonValueChange)="onMacroChange()" input-sequence></cs-input-measure>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedMacro(recommendedProtein) })
          }}</cs-note>
      </cs-form-field>

      <!-- Carbs & Fat — Fixed mode: slider + side-by-side inputs -->
      @if (!isFlexible) {
      <cs-form-field [label]="LL.editNutritionPlan.carbFatRatioLabel()">
        <cs-slider [value]="carbFatRatio" (valueChange)="onCarbFatRatioChange($event)" e2e-locator="carb-fat-ratio"
          [min]="20" [max]="80" [step]="1" [showPercentSign]="true" [tickmarks]="carbFatTickmarks"
          [highlightedValues]="carbFatHighlightedValues"
          [customBackgroundGradientMarks]="carbFatGradientMarks"></cs-slider>
        <help-icon name="info" helpId="@help:references.carb-fat-ratio"></help-icon>
      </cs-form-field>
      <cs-form-field [label]="LL.editNutritionPlan.carbsLabel()">
        <div class="input-with-calories">
          <cs-input-measure class="fixed-input" [(canonValue)]="carbs" type="nutrient_g" [precisionOverride]="1"
            (canonValueChange)="onCarbsManualChange()" input-sequence></cs-input-measure>
          <cs-note light>{{ getCarbsCaloriesFormatted() }}</cs-note>
        </div>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedMacro(recommendedCarbs) })
          }}</cs-note>
      </cs-form-field>
      <cs-form-field [label]="LL.editNutritionPlan.fatLabel()">
        <div class="input-with-calories">
          <cs-input-measure class="fixed-input" [(canonValue)]="fat" type="nutrient_g" [precisionOverride]="1"
            (canonValueChange)="onFatManualChange()" input-sequence></cs-input-measure>
          <cs-note light>{{ getFatCaloriesFormatted() }}</cs-note>
        </div>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedMacro(recommendedFat) })
          }}</cs-note>
      </cs-form-field>
      }

      <!-- Carbs & Fat — Flexible mode: separate inputs -->
      @if (isFlexible) {
      <cs-form-field [label]="LL.editNutritionPlan.carbsLabel()">
        <div class="input-with-calories">
          <cs-input-measure class="fixed-input" [(canonValue)]="carbs" type="nutrient_g" [precisionOverride]="1"
            (canonValueChange)="onMacroChange()" input-sequence></cs-input-measure>
          <cs-note light>{{ getCarbsCaloriesFormatted() }}</cs-note>
        </div>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedMacro(recommendedCarbs) })
          }}</cs-note>
      </cs-form-field>
      <cs-form-field [label]="LL.editNutritionPlan.fatLabel()">
        <div class="input-with-calories">
          <cs-input-measure class="fixed-input" [(canonValue)]="fat" type="nutrient_g" [precisionOverride]="1"
            (canonValueChange)="onMacroChange()" input-sequence></cs-input-measure>
          <cs-note light>{{ getFatCaloriesFormatted() }}</cs-note>
        </div>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedMacro(recommendedFat) })
          }}</cs-note>
      </cs-form-field>
      }

      <!-- Fiber -->
      <cs-form-field [label]="LL.editNutritionPlan.fiberLabel()">
        <cs-input-measure class="fixed-input" [(canonValue)]="fiber" type="nutrient_g" [precisionOverride]="1"
          (canonValueChange)="onMacroChange()" input-sequence></cs-input-measure>
        <cs-note>{{ LL.editNutritionPlan.recommendedHint({ value: getFormattedRecommendedMacro(recommendedFiber) })
          }}</cs-note>
      </cs-form-field>

      @if (hasCalorieMismatch()) {
      <cs-text-alert type="warning" [message]="LL.editNutritionPlan.calorieOvershoot()"></cs-text-alert>
      }
    </div>
  </cs-item-list>
</cs-content>

<cs-page-action-buttons [showCancel]="true" (cancel)="handleCancel($event)">
  <cs-button medium primary [label]="LL.common.buttons.save()" (click)="handleSave($event)"></cs-button>
</cs-page-action-buttons>